/**
 * The last line of defence: a React crash anywhere under the shell.
 *
 * Without this, a throw during render unmounts the whole tree and leaves a blank page — which is
 * the single worst thing this app can show, because a player cannot tell it from a dead server and
 * has nothing to do but close the tab. The card keeps the brand on screen, says plainly that the
 * fault is ours, and offers the one action that reliably works.
 *
 * Crashes are reported to `POST /api/track` as `client_error`, which is on the server's whitelist
 * with wider caps for `message` and `stack` and a limit of five per session per minute
 * (`packages/server/src/http/trackRoute.ts`, docs/ANALYTICS.md §2). The route is bearer-only, so
 * the stored session JWT has to ride along: with the cookie alone every report was answered 401
 * and dropped. Failures stay silent either way — a reporting failure must never become a second
 * visible error.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { apiPath, isEmbedded } from '../bridge/embedded';
import { gameHome } from './paths';
import { readStoredToken } from '../bridge/identity';
import { toErrorReport, type ErrorReport } from './states';
import { Button, Panel } from './ui';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Injected by tests. */
  report?: (report: ErrorReport) => void;
}

interface State {
  failed: boolean;
  message: string;
}

/** Fire-and-forget crash report. Never throws, never retries, never blocks the card. */
export function reportClientError(report: ErrorReport): void {
  try {
    const token = readStoredToken();
    void fetch(apiPath('/api/track'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify({ ev: 'client_error', props: report }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no network, blocked fetch, or a browser that dislikes keepalive: the console still has it */
  }
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { failed: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const report = toErrorReport(error, info.componentStack);
    // The console first: it is the only channel that survives an offline browser, and it is where
    // anyone debugging will look.
    console.error('[shell] uncaught error', error, info.componentStack);
    (this.props.report ?? reportClientError)(report);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="screen screen--center" data-testid="crash">
        <div className="screen__inner" style={{ alignItems: 'center', textAlign: 'center' }}>
          <div className="wobble" aria-hidden style={{ fontSize: 56, lineHeight: 1 }}>
            ♠️
          </div>
          <h1 className="display display--lg">Something broke</h1>
          <p className="muted" style={{ margin: 0 }}>
            That one is on us, not you. A reload usually sorts it, and your table is still there.
          </p>
          <Panel felt style={{ width: '100%', maxWidth: 360 }}>
            <div className="stack">
              <Button variant="primary" size="lg" block testId="crash-reload" onClick={() => location.reload()}>
                Reload
              </Button>
              {/* The game's own address: `/` is the landing site on the web. Inside Discord the query
                  carries the Activity's `frame_id`, which the server needs to serve the game. */}
              <a className="link small" href={isEmbedded() ? `/${location.search}` : gameHome()}>
                Back to the lobby
              </a>
            </div>
          </Panel>
          {/* The message, not the stack: enough for a player to quote in a bug report, not enough
              to look like a core dump. */}
          {this.state.message && (
            <p className="small muted" data-testid="crash-message" style={{ margin: 0, maxWidth: 420, wordBreak: 'break-word' }}>
              {this.state.message}
            </p>
          )}
        </div>
      </div>
    );
  }
}
